import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getTasks, updateTask } from '../api';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { CheckCircle2, Circle, RefreshCw, ExternalLink } from 'lucide-react';

const Tasks = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending'); // pending, completed, all

  const isManager = user?.role === 'manager';

  const fetchTasks = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (filter === 'pending') params.completed = false;
      if (filter === 'completed') params.completed = true;
      
      const response = await getTasks(params);
      setTasks(response.data);
    } catch (error) {
      toast.error('Failed to fetch tasks');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  const handleToggleTask = async (taskId, currentCompleted) => {
    try {
      await updateTask(taskId, !currentCompleted);
      toast.success(currentCompleted ? 'Task marked as pending' : 'Task completed');
      fetchTasks();
    } catch (error) {
      toast.error('Failed to update task');
    }
  };

  const getTaskTypeBadge = (type) => {
    const styles = {
      'new_account': 'bg-blue-500/10 text-blue-400 border-blue-500/50',
      'handoff_received': 'bg-purple-500/10 text-purple-400 border-purple-500/50',
      'blocker_approval': 'bg-warning/10 text-warning border-warning/50',
      'verify_blockers': 'bg-orange-500/10 text-orange-400 border-orange-500/50',
    };
    return styles[type] || 'bg-zinc-500/10 text-zinc-400 border-zinc-500/50';
  };

  const formatTaskType = (type) => {
    const labels = {
      'new_account': 'New Account',
      'handoff_received': 'Hand-off Received',
      'blocker_approval': 'Blocker Approval',
      'verify_blockers': 'Verify Blockers',
    };
    return labels[type] || type;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-heading font-bold text-text-primary">
            {isManager ? 'Manager Tasks' : 'My Tasks'}
          </h1>
          <p className="text-text-secondary text-sm mt-1">
            {tasks.length} task{tasks.length !== 1 ? 's' : ''}
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={fetchTasks}
            className="p-2 rounded-md bg-surface border border-border text-text-secondary hover:text-text-primary hover:bg-surface-highlight transition-all"
            data-testid="refresh-tasks"
          >
            <RefreshCw size={20} />
          </button>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-2 bg-surface p-1 rounded-lg border border-border w-fit" data-testid="task-filters">
        {['pending', 'completed', 'all'].map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              filter === f
                ? 'bg-primary text-primary-foreground'
                : 'text-text-secondary hover:text-text-primary hover:bg-surface-highlight'
            }`}
            data-testid={`filter-${f}`}
          >
            {f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}
      </div>

      {/* Tasks List */}
      <div className="bg-surface border border-border rounded-lg overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="flex items-center justify-center gap-2 text-text-secondary">
              <div className="w-5 h-5 border-2 border-primary/30 border-t-primary rounded-full spinner" />
              Loading...
            </div>
          </div>
        ) : tasks.length === 0 ? (
          <div className="p-8 text-center text-text-secondary">
            No {filter === 'all' ? '' : filter} tasks
          </div>
        ) : (
          <div className="divide-y divide-border">
            {tasks.map((task) => (
              <div
                key={task.id}
                className="p-4 flex items-start gap-4 hover:bg-surface-highlight/50 transition-colors"
                data-testid={`task-${task.id}`}
              >
                <button
                  onClick={() => handleToggleTask(task.id, task.completed)}
                  className={`mt-0.5 flex-shrink-0 transition-colors ${
                    task.completed
                      ? 'text-success hover:text-success/80'
                      : 'text-text-secondary hover:text-primary'
                  }`}
                  data-testid={`toggle-task-${task.id}`}
                >
                  {task.completed ? (
                    <CheckCircle2 size={22} />
                  ) : (
                    <Circle size={22} />
                  )}
                </button>
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border ${getTaskTypeBadge(task.task_type)}`}>
                      {formatTaskType(task.task_type)}
                    </span>
                    <span className="text-text-secondary text-xs">
                      {task.team}
                    </span>
                  </div>
                  <p className={`text-text-primary ${task.completed ? 'line-through opacity-60' : ''}`}>
                    {task.description}
                  </p>
                  <div className="flex items-center gap-3 mt-2 text-xs text-text-secondary">
                    <span className="font-mono">{task.account_number}</span>
                    <span>{task.account_name}</span>
                    <span>•</span>
                    <span>{format(new Date(task.created_at), 'MMM d, h:mm a')}</span>
                  </div>
                </div>

                <button
                  onClick={() => navigate(`/accounts/${task.account_id}`)}
                  className="p-2 rounded-md text-text-secondary hover:text-primary hover:bg-primary/10 transition-colors"
                  data-testid={`view-account-${task.id}`}
                >
                  <ExternalLink size={18} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Tasks;
